import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useDropdownOptions } from "../../../../../hooks/useDropdownOptions";
import { useSystemDropdownOptions } from "../../../../../hooks/useSystemDropdownOptions";
import { SystemDropdownCategory, SystemDropdownParameter } from "../../../../api/ClientFileApi";
import {
  CLAIM_STATUS_OPTIONS,
  CLAIM_STATUS_YES_NO_OPTIONS,
  ClaimStatusKey,
  claimStatusLabelKey,
} from "../../../claimStatus";
import { ClaimFormOptions } from "../ClaimFormFields.types";

export function useClaimFormOptions(): ClaimFormOptions {
  const { t } = useTranslation();
  const { claimLegalBases } = useDropdownOptions();
  const entryTypes = useSystemDropdownOptions(SystemDropdownCategory.ClaimBalanceEntryType);

  // Disputed (Strittig), collateral (Sicherheiten), entirely disputed (Vollständig bestritten) and
  // SRV registration (Im SRV angemeldet) share one fixed Ja/Nein/Unbekannt set, and the two optional
  // fields leave out Unbekannt. Memoized for the same reason useSystemDropdownOptions is: the arrays
  // reach Formik fields that re-render on every keystroke.
  const claimStatusOptions = useMemo(
    () => CLAIM_STATUS_OPTIONS.map((status) => ({ id: status, label: t(claimStatusLabelKey(status)) })),
    [t]
  );
  // The Sicherheiten question names its third answer "Weiß nicht", as the story and the legacy form do.
  const collateralStatusOptions = useMemo(
    () =>
      CLAIM_STATUS_OPTIONS.map((status) => ({
        id: status,
        label:
          status === ClaimStatusKey.Unknown ? t("claims.collaterals.statusUnknown") : t(claimStatusLabelKey(status)),
      })),
    [t]
  );
  const yesNoOptions = useMemo(
    () => CLAIM_STATUS_YES_NO_OPTIONS.map((status) => ({ id: status, label: t(claimStatusLabelKey(status)) })),
    [t]
  );

  return {
    legalBasisOptions: claimLegalBases.map((v) => ({ id: v, label: v })),
    disputedStatusOptions: claimStatusOptions,
    collateralStatusOptions,
    claimEntirelyDisputedOptions: yesNoOptions,
    srvRegistrationStatusOptions: yesNoOptions,
    entryTypeOptions: entryTypes.optionItems,
    // Which type records the amount filed in the SRV is a parameter a Supervisor sets, not a fixed
    // key — the same source the backend rule reads.
    isSrvRegisteredEntryType: (entryType: string | null | undefined) =>
      entryTypes.booleanParameterOf(entryType, SystemDropdownParameter.SrvRegistered),
  };
}
